/**
 * Abrechnungs-Beleg für einen Tag — druckbar / als PDF speicherbar.
 *
 * Bewusst ohne PDF-Library: der Beleg wird als „Papier"-Ansicht gerendert und
 * über die native Druckfunktion des Browsers (window.print) ausgegeben. Auf
 * Android-Chrome und Desktop lässt sich daraus „Als PDF speichern" wählen.
 * Funktioniert vollständig offline.
 *
 * Das Print-CSS (siehe styles/index.css, @media print) blendet beim Drucken
 * alles außer `#print-area` aus.
 */

import type { LocalShift, LocalLocation } from '../db/schema';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import { isOpenEndPending, shiftHours } from '../lib/payroll';

interface Props {
  isoDate: string;
  shifts: LocalShift[];
  locationsById: Map<string, LocalLocation>;
  /** Name des Mitarbeiters (für die Beleg-Kopfzeile). */
  employeeName?: string;
  breakDeduction: boolean;
  onClose: () => void;
}

function fmtHours(h: number): string {
  return `${h.toFixed(2).replace('.', ',')} h`;
}

export function ReceiptModal({
  isoDate,
  shifts,
  locationsById,
  employeeName,
  breakDeduction,
  onClose,
}: Props) {
  // Stornierte Schichten gehören nicht auf den Beleg.
  const rows = shifts.filter((s) => s.status !== 'cancelled');

  let totalHours = 0;
  let totalWage = 0;
  // Lohn kann für eine fremde Schicht `null` sein (Filialleitung ohne
  // managerCanSeeWages-Freigabe) — solche Posten fließen NICHT als 0 in die
  // Summe ein (das würde "0,00 €" vortäuschen), sondern setzen nur das
  // wageIncomplete-Flag, das die Summe mit einem Hinweis-Stern markiert.
  let wageIncomplete = false;
  for (const s of rows) {
    const h = shiftHours(s, { breakDeduction });
    totalHours += h;
    if (s.hourlyWage == null) {
      wageIncomplete = true;
    } else {
      totalWage += h * s.hourlyWage;
    }
  }
  totalWage = Math.round(totalWage * 100) / 100;
  totalHours = Math.round(totalHours * 100) / 100;

  const settledRows = rows.filter((s) => !!s.settledAt);
  const allSettled = rows.length > 0 && settledRows.length === rows.length;
  const someSettled = settledRows.length > 0 && !allSettled;
  // Jüngstes Abrechnungsdatum für die Statuszeile.
  const settledDate = settledRows
    .map((s) => s.settledAt as string)
    .sort()
    .at(-1);

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center p-0 sm:p-4 z-[60] overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white text-slate-900 w-full max-w-md sm:rounded-lg shadow-xl my-0 sm:my-4">
        <div id="print-area" className="p-6">
          <div className="flex items-baseline justify-between border-b border-slate-300 pb-3 mb-4">
            <h2 className="text-lg font-bold">Arbeitszeit-Nachweis</h2>
            <span className="text-sm text-slate-500">ShiftSync</span>
          </div>

          <div className="text-sm mb-4 space-y-0.5">
            {employeeName && (
              <p>
                <span className="text-slate-500">Mitarbeiter:</span>{' '}
                <span className="font-medium">{employeeName}</span>
              </p>
            )}
            <p>
              <span className="text-slate-500">Datum:</span>{' '}
              <span className="font-medium">{formatDate(isoDate)}</span>
            </p>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-slate-500">Keine abrechenbaren Schichten an diesem Tag.</p>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-300 text-left text-slate-500">
                  <th className="py-1 font-medium">Standort</th>
                  <th className="py-1 font-medium">Zeit</th>
                  <th className="py-1 font-medium text-right">Std.</th>
                  <th className="py-1 font-medium text-right">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const loc = locationsById.get(s.locationId);
                  const h = shiftHours(s, { breakDeduction });
                  const wage = s.hourlyWage == null ? null : Math.round(h * s.hourlyWage * 100) / 100;
                  const open = isOpenEndPending(s);
                  return (
                    <tr key={s.id} className="border-b border-slate-200 align-top">
                      <td className="py-1.5 pr-2">
                        {loc?.name ?? '—'}
                        {s.settledAt && (
                          <span className="ml-1 text-[10px] text-emerald-700">✓ abger.</span>
                        )}
                        {s.status === 'planned' && (
                          <span className="ml-1 text-[10px] text-slate-400">(geplant)</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {open ? `ab ${s.startTime}` : `${s.startTime}–${s.endTime}`}
                        {s.bonusHours > 0 && (
                          <span className="text-slate-400">
                            {' '}+{s.bonusHours.toFixed(2).replace('.', ',')}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 text-right whitespace-nowrap">{fmtHours(h)}</td>
                      <td className="py-1.5 text-right whitespace-nowrap">{formatMoney(wage)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-2" colSpan={2}>
                    Summe ({rows.length} {rows.length === 1 ? 'Schicht' : 'Schichten'})
                  </td>
                  <td className="py-2 text-right">{fmtHours(totalHours)}</td>
                  <td className="py-2 text-right">
                    {formatMoney(totalWage)}
                    {wageIncomplete && (
                      <span
                        className="text-amber-600"
                        title="Lohn für mindestens eine Schicht nicht sichtbar (Standort-Einstellung)"
                      >
                        {' '}*
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          <div className="mt-4 pt-3 border-t border-slate-300 text-xs text-slate-500 space-y-1">
            <p>
              Status:{' '}
              {allSettled ? (
                <span className="text-emerald-700 font-medium">
                  abgerechnet{settledDate ? ` am ${formatDateTime(settledDate)}` : ''}
                </span>
              ) : someSettled ? (
                <span className="text-amber-700 font-medium">teilweise abgerechnet</span>
              ) : (
                <span className="text-slate-600 font-medium">offen</span>
              )}
            </p>
            <p>Brutto = Netto (Minijob, keine Steuerabzüge).</p>
          </div>
        </div>

        <div className="no-print flex gap-2 p-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="flex-1 rounded py-2 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            Schließen
          </button>
          <button
            onClick={() => window.print()}
            className="flex-1 rounded py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Drucken / Als PDF
          </button>
        </div>
      </div>
    </div>
  );
}
